import React, { useCallback, useEffect, useState } from 'react';
import { FormApi } from 'final-form';
import * as yup from 'yup';
import { CircularProgress } from '@material-ui/core';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import Form from 'ui/Form/Form';
import Button from 'ui/Button/Button';
import { IAccount } from 'store/accounts.model';
import { AxiosStatus } from 'api/axiosConfig';
import FieldsLeveler from 'ui/structure/FieldsLeveler/FieldsLeveler';
import SubmitLeveler from 'ui/structure/SubmitLeveler/SubmitLeveler';
import { changeFieldValue, setFieldTouched } from 'utils/formMutators';
import { requiredOnly, validateAccountFAB, validateAmount, validationCallback } from 'utils/validation';
import {
  ITransactionField,
  ITransferResponse,
  sortWithScheduleArrays,
} from 'components/modules/Transfers/tabs/ToFirstAtlantic/ToFirstAtlantic.model';
import { getTransactionsArray } from 'utils/mapping/transactionCardMapper';
import api from 'api/api';
import { IApiResponse } from 'utils/models';
import { NETWORK_REQUIRED } from 'utils/constants';
import routePaths from 'routes/routePaths';
import validateOtpPin, { handlePinError } from 'utils/otpValidation';
import { OTPConfirmationModal, StatusModal } from 'components/modals';
import { IOTPError } from 'components/authModules/LogIn/login.model';
import { getReceipt } from 'components/modules/Transfers/tabs/ToFirstAtlantic/submitApi';
import { renderOwnTransferFieldsTo, renderTransferFieldsFrom } from '../EditScheduleTransferFIelds';
import { IFormValues, IScheduleTransfer } from '../ScheduledTransfers.model';

interface IProps {
  accounts: IAccount[];
  selectedData: IScheduleTransfer | undefined;
}

const schema = yup.object().shape({
  sourceAccount: requiredOnly('Source Account'),
  amount: validateAmount(),
  destinationAccountNo: validateAccountFAB('Account Number'),
  narration: requiredOnly('Narration'),
  scheduleTransfer: yup.boolean(),
  frequency: yup.string().when('scheduleTransfer', {
    is: true,
    then: requiredOnly('Frequency'),
  }),
  startDate: yup.string().test('startDate', 'Special Date is required', (value, context) => {
    const FABValues: IFormValues = context.parent as IFormValues;
    if (FABValues?.frequency === 'specialDate') {
      return !!value;
    }
    return true;
  }),
});

const FirstAtlanticAccounts = ({ accounts, selectedData }: IProps) => {
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const [previewData, setPreviewData] = useState<ITransactionField[]>([]);
  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [transactionId, setTransactionId] = useState<number>(NaN);
  const [OTPError, setOTPError] = useState<IOTPError | null>(null);
  const [successMsg, setSuccessMsg] = useState<string>('');
  const submitting = submitStatus === AxiosStatus.PENDING;
  const showStatusModal = submitStatus === AxiosStatus.SUCCESS || submitStatus === AxiosStatus.FAILURE;

  let mainForm!: FormApi<IFormValues, Partial<IFormValues>>;
  const validate = useCallback((values: IFormValues) => validationCallback<unknown>(schema)(values), []);

  const dateFilterChange = (name: string, value?: string | null | undefined) => {
    mainForm.change('startDate', value as string);
  };

  const handleClearDateInput = (name: string, value?: string | null | undefined) => {
    mainForm.change('startDate', value as string);
  };

  const history = useHistory();
  const handleOnSuccess = () => {
    history.push(routePaths.dashboard);
  };

  const openConfirmation = () => setConfirmationOpen(true);
  const closeConfirmation = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    } else {
      setConfirmationOpen(false);
    }
  };

  const onRejectedSubmit = useCallback((error: IApiResponse) => {
    console.error(error || NETWORK_REQUIRED);
    setErrorMsg(error.description || NETWORK_REQUIRED);
    setSubmitStatus(AxiosStatus.FAILURE);
    closeConfirmation();
  }, []);

  const onCloseStatusModal = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    }
    setSubmitStatus(AxiosStatus.DEFAULT);
  };

  const onSubmit = (values: IFormValues) => {
    const readyValues = {
      sourceAccount: values.sourceAccount,
      narration: values.narration,
      // destinationAccountName: values.destinationAccountName,
      // eLevy: elevyState.charge,
      // totalAmount: Number(values.amount) + Number(values.eLevyFee),
      transactionAmount: values.amount,
      frequency: values.frequency === 'specialDate' ? 'SPECIAL DATE' : values?.frequency?.toLocaleUpperCase(),
      startDate: values.startDate || '',
    };

    setPreviewData(getTransactionsArray(readyValues, undefined, undefined, sortWithScheduleArrays));
    api
      .post('/customer/otp/generate')
      .then(() => {
        openConfirmation();
      })
      .catch((data) => {
        onRejectedSubmit(data);
      });
    setSubmitStatus(AxiosStatus.DEFAULT);
    openConfirmation();
  };

  const onConfirm = useCallback(
    (values: IFormValues) => (otp: string, saveBeneficiary: boolean) => {
      setErrorMsg('');
      setSubmitStatus(AxiosStatus.PIN_PENDING);

      return validateOtpPin(otp)
        .then(() => {
          setSubmitStatus(AxiosStatus.PENDING);
          const sourceAccount = accounts.find((a) => a.accountNumber === values.sourceAccount);

          const requestBody = {
            ...values,
            password: otp,
            passwordType: 'OTP',
            transType: 'LOCAL-TRANSFER',
            currencyCode: sourceAccount?.currency,
            // sourceAccountType: sourceAccount?.accountType || DEFAULT_ACCOUNT_TYPE,
            saveBen: saveBeneficiary,
            isSchedule: true,
          };

          api
            .post('transfer/local', requestBody)
            .then((response: ITransferResponse) => {
              setSuccessMsg(response.description || 'Transfer Completed Successfully!');
              setSubmitStatus(AxiosStatus.SUCCESS);
              closeConfirmation();
              setTransactionId(response.transactionId);
            })
            .catch((error: IApiResponse) => {
              console.error(error);
              setErrorMsg(error.description || 'Transfer is failed. Unable to complete the transaction.');
              setSubmitStatus(AxiosStatus.FAILURE);
              closeConfirmation();
            });
        })
        .catch((error: IOTPError) => {
          setOTPError(error);
          handlePinError(setSubmitStatus, setErrorMsg);
        });
    },
    [accounts],
  );

  const onResend = () => api.post('/customer/otp/generate');

  useEffect(() => {
    if (selectedData == null || !selectedData) {
      return;
    }

    mainForm.change('sourceAccount', String(selectedData.sourceAccount));
    mainForm.change('destinationAccountNo', selectedData?.destinationAccountNo);
    mainForm.change('amount', String(selectedData.transactionAmount));
    mainForm.change('narration', selectedData.transactionNarration);
    mainForm.change('frequency', selectedData.frequency);

    dateFilterChange('startDate', moment(selectedData.createDate).format('YYYY-MM-DD'));
  }, [selectedData]);

  const onRetryConfirm = () => setSubmitStatus(AxiosStatus.DEFAULT);
  const onGetReceipt = useCallback(() => getReceipt(transactionId), [transactionId]);

  return (
    <Form
      onSubmit={onSubmit}
      validate={validate}
      mutators={{ changeFieldValue, setFieldTouched }}
      render={({ handleSubmit, values, form, hasValidationErrors }) => {
        mainForm = mainForm || form;
        return (
          <form onSubmit={handleSubmit}>
            <FieldsLeveler label="Transfer From" dividerBottom>
              {renderTransferFieldsFrom(accounts)}
            </FieldsLeveler>
            <FieldsLeveler label="Transfer To">
              {renderOwnTransferFieldsTo(accounts, values, submitting, dateFilterChange, handleClearDateInput)}
            </FieldsLeveler>
            <SubmitLeveler>
              <Button type="submit" disabled={submitting || hasValidationErrors} fullWidth>
                {submitting ? <CircularProgress size={20} color="inherit" /> : 'Save'}
              </Button>
            </SubmitLeveler>
            <OTPConfirmationModal
              open={isConfirmationOpen}
              data={previewData}
              onClose={closeConfirmation}
              onResend={onResend}
              OTPPinError={OTPError}
              onSubmit={onConfirm(values)}
              submitStatus={submitStatus}
              onSuccess={handleOnSuccess}
              onRetry={onRetryConfirm}
              getReceipt={onGetReceipt}
              successText={successMsg}
              failureText={errorMsg}
            />
            <StatusModal
              open={showStatusModal}
              status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
              onClose={onCloseStatusModal}
              onRetry={onCloseStatusModal}
              onRetryText="Close"
            >
              {submitStatus === AxiosStatus.SUCCESS ? successMsg : errorMsg}
            </StatusModal>
          </form>
        );
      }}
    />
  );
};

export default FirstAtlanticAccounts;
